if (!customElements.get('tp-main-product')) {
  class TpMainProduct extends HTMLElement {
    connectedCallback() {
      this.config = this.parseConfig();
      if (!this.config) return;
      this.config.tiers = this.getNormalizedTiers(this.config.tiers);

      this.selectedSize = this.getInitialSize();
      this.sizeOptions = Array.from(this.querySelectorAll('[data-size-option]'));
      this.galleryPanels = Array.from(this.querySelectorAll('[data-gallery-panel]'));
      this.galleryThumbs = Array.from(this.querySelectorAll('[data-gallery-thumb]'));
      this.flavorRows = Array.from(this.querySelectorAll('[data-flavor-row]')).map((row, index) => ({
        row,
        index,
        label: row.dataset.flavorLabel || '',
        qtyValue: row.querySelector('[data-qty-value]'),
        decrease: row.querySelector('[data-qty-decrease]'),
        increase: row.querySelector('[data-qty-increase]'),
        openNutrition: row.querySelector('[data-open-nutrition]'),
        variants: this.parseJsonNode(row.querySelector('[data-flavor-variants]')) || [],
        nutrition: this.parseJsonNode(row.querySelector('[data-flavor-nutrition]')) || null,
        quantity: Number(row.querySelector('[data-qty-value]')?.textContent || 0),
        selectedVariant: null,
      }));

      this.tiers = Array.from(this.querySelectorAll('[data-tier]'));
      this.tierHeader = this.querySelector('.tp-main-product__tier-header');
      this.summaryCompare = this.querySelector('[data-summary-compare]');
      this.summaryCurrent = this.querySelector('[data-summary-current]');
      this.summaryBadge = this.querySelector('[data-summary-badge]');
      this.buyButton = this.querySelector('[data-buy-button]');
      this.buyButtonWrap = this.querySelector('.tp-main-product__buy-button-wrap');
      this.nutritionModal = document.getElementById(`TpNutritionModal-${this.dataset.sectionId}`);
      this.nutritionModalTitle = this.nutritionModal?.querySelector('[data-nutrition-modal-title]');
      this.nutritionModalContent = this.nutritionModal?.querySelector('[data-nutrition-modal-content]');
      this.nutritionModalGuarantees = this.nutritionModal?.querySelector('[data-nutrition-modal-guarantees]');
      this.servingQuizzes = Array.from(this.querySelectorAll('[data-serving-quiz]'));
      this.cartSectionsUrl = this.dataset.cartSectionsUrl || '/?sections=cart-drawer,cart-icon-bubble';
      this.originalBuyLabel = this.buyButton ? this.buyButton.textContent.trim() : '';
      this.handleResize = this.updateTierProgress.bind(this);

      this.applyTierLayout();

      this.bindEvents();
      this.initServingQuizzes();
      this.syncState();
      window.addEventListener('resize', this.handleResize);
    }

    disconnectedCallback() {
      window.removeEventListener('resize', this.handleResize);
    }

    parseConfig() {
      return this.parseJsonNode(this.querySelector('[data-tp-main-product-config]'));
    }

    parseJsonNode(node) {
      if (!node) return null;

      try {
        return JSON.parse(node.textContent);
      } catch (error) {
        console.error('tp-main-product: invalid JSON config', error);
        return null;
      }
    }

    getNormalizedTiers(tiers) {
      if (!Array.isArray(tiers)) return [];

      const seenQuantities = new Set();

      return tiers
        .map((tier) => ({
          ...tier,
          quantity: Number.parseInt(tier?.quantity, 10) || 0,
          discount: Number.parseFloat(tier?.discount) || 0,
        }))
        .filter((tier) => tier.quantity > 0)
        .sort((left, right) => left.quantity - right.quantity)
        .filter((tier) => {
          if (seenQuantities.has(tier.quantity)) return false;
          seenQuantities.add(tier.quantity);
          return true;
        });
    }

    getTierQuantities() {
      return this.tiers
        .map((tier) => Number.parseInt(tier.dataset.tierQuantity || '0', 10))
        .filter((quantity) => quantity > 0);
    }

    getFirstCheckpointQuantity() {
      return this.config?.tiers?.[0]?.quantity || this.getTierQuantities()[0] || 0;
    }

    canPurchase(totalQuantity) {
      const firstCheckpointQuantity = this.getFirstCheckpointQuantity();

      if (firstCheckpointQuantity <= 0) {
        return totalQuantity > 0;
      }

      return totalQuantity >= firstCheckpointQuantity;
    }

    applyTierLayout() {
      if (!this.tierHeader) return;

      const tierColumns = Math.max(2, this.tiers.length);
      this.tierHeader.style.setProperty('--tp-tier-columns', String(tierColumns));
    }

    getInitialSize() {
      const selectedOption = this.querySelector('[data-size-option].is-active');
      return selectedOption?.dataset.sizeValue || this.querySelector('[data-size-option]')?.dataset.sizeValue || '';
    }

    bindEvents() {
      this.sizeOptions.forEach((button) => {
        button.addEventListener('click', () => {
          if (button.disabled) return;
          this.selectedSize = button.dataset.sizeValue || '';
          this.resetAllQuantities();
          this.updateSizeButtons();
          this.updateGalleryFromSize(button.dataset.sizeMediaId);
          this.syncState();
        });
      });

      this.galleryThumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          this.setActiveMedia(thumb.dataset.mediaId);
        });
      });

      this.flavorRows.forEach((flavor) => {
        flavor.decrease?.addEventListener('click', () => {
          this.updateFlavorQuantity(flavor, -1);
        });

        flavor.increase?.addEventListener('click', () => {
          this.updateFlavorQuantity(flavor, 1);
        });

        flavor.openNutrition?.addEventListener('click', () => {
          this.openNutritionModal(flavor, flavor.openNutrition);
        });
      });

      this.buyButton?.addEventListener('click', () => {
        this.addSelectionToCart();
      });
    }

    initServingQuizzes() {
      this.servingQuizzes.forEach((quiz) => {
        if (quiz.dataset.initialized === 'true') return;
        quiz.dataset.initialized = 'true';

        const formPanel = quiz.querySelector('[data-serving-quiz-form-panel]');
        const resultPanel = quiz.querySelector('[data-serving-quiz-result-panel]');
        const form = quiz.querySelector('[data-serving-quiz-form]');
        const weightInput = quiz.querySelector('[data-serving-quiz-weight]');
        const resultIntro = quiz.querySelector('[data-serving-quiz-result-intro]');
        const resultValue = quiz.querySelector('[data-serving-quiz-result-value]');
        const resultCopy = quiz.querySelector('[data-serving-quiz-result-copy]');
        const resetButton = quiz.querySelector('[data-serving-quiz-reset]');
        const suffix = quiz.dataset.resultSuffix || 'por dia.';

        const showForm = () => {
          if (formPanel) formPanel.hidden = false;
          if (resultPanel) resultPanel.hidden = true;
        };

        const showResult = () => {
          if (formPanel) formPanel.hidden = true;
          if (resultPanel) resultPanel.hidden = false;
        };

        const resetQuiz = () => {
          form?.reset();
          showForm();
        };

        form?.addEventListener('submit', (event) => {
          event.preventDefault();

          const weight = Number(weightInput?.value || 0);

          if (!weight || weight <= 0) {
            showForm();
            return;
          }

          const calculation = window.TpServingQuiz?.calculate(weight, this.config?.sizeSourceVariants);

          if (!calculation) {
            showForm();
            return;
          }

          const { gramsPerDay, coefficient, packageSummary } = calculation;

          if (resultIntro && !resultIntro.textContent.trim()) {
            resultIntro.textContent = `Para um cão com ${window.TpServingQuiz.formatWeight(weight)} kg, a recomendação média é de:`;
          }

          if (resultValue) {
            resultValue.textContent = `${gramsPerDay}g ${suffix}`.trim();
          }

          if (resultCopy && !resultCopy.textContent.trim()) {
            const copyParts = [
              `Coeficiente K utilizado: ${coefficient}. O cálculo aplica 87 × peso^0,75.`,
            ];

            if (packageSummary) {
              copyParts.push(packageSummary);
            }

            resultCopy.textContent = copyParts.join(' ');
          }

          showResult();
        });

        resetButton?.addEventListener('click', () => {
          resetQuiz();
        });

        const modal = quiz.closest('[data-serving-quiz-modal]');
        const trigger = this.querySelector(`[data-modal="#${modal?.id}"] [data-serving-quiz-trigger]`);
        trigger?.addEventListener('click', () => {
          resetQuiz();
        });
      });
    }

    resetAllQuantities() {
      this.flavorRows.forEach((flavor,index) => {
        flavor.quantity = index < 3 ? 1 : 0;
    if (flavor.qtyValue) {
      flavor.qtyValue.textContent = String(flavor.quantity);
        }
      });
    }

    updateSizeButtons() {
      this.sizeOptions.forEach((button) => {
        const isActive = button.dataset.sizeValue === this.selectedSize;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
      });
    }

    isSizeAvailable(sizeValue) {
      return this.flavorRows.some((flavor) => {
        const matchingVariant = this.findMatchingVariant(flavor.variants, sizeValue);
        return matchingVariant?.available;
      });
    }

    updateSizeAvailability() {
      this.sizeOptions.forEach((button) => {
        const isAvailable = this.isSizeAvailable(button.dataset.sizeValue || '');
        button.disabled = !isAvailable;
        button.dataset.available = isAvailable ? 'true' : 'false';
      });
    }

    updateGalleryFromSize(mediaId) {
      if (!mediaId || mediaId === '0') return;
      this.setActiveMedia(mediaId);
    }

    setActiveMedia(mediaId) {
      const mediaGallery = this.querySelector('media-gallery');
      if (mediaGallery?.setActiveMedia && this.dataset.sectionId) {
        mediaGallery.setActiveMedia(`${this.dataset.sectionId}-${mediaId}`, false);
        return;
      }

      this.galleryPanels.forEach((panel) => {
        const isActive = panel.dataset.mediaId === mediaId;
        panel.classList.toggle('is-active', isActive);
        panel.hidden = !isActive;
      });

      this.galleryThumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', thumb.dataset.mediaId === mediaId);
      });
    }

    updateFlavorQuantity(flavor, delta) {
      const nextValue = Math.max(0, flavor.quantity + delta);

      if (delta > 0 && (!flavor.selectedVariant || !flavor.selectedVariant.available)) {
        return;
      }

      flavor.quantity = nextValue;
      if (flavor.qtyValue) {
        flavor.qtyValue.textContent = String(nextValue);
      }
      this.syncState();
    }

    openNutritionModal(flavor, opener) {
      if (!this.nutritionModal || !this.nutritionModalContent || !this.nutritionModalGuarantees) return;

      const nutrition = flavor?.nutrition || {};
      const modalTitle = flavor?.label || 'Tabela nutricional';
      const flavorColor = flavor?.row?.dataset?.flavorColor || '';
      const blocks = [
        { title: 'Ingredientes', description: this.formatNutritionText(nutrition.ingredients) || 'Não informado.' },
        { title: 'Suplementação', description: this.formatNutritionText(nutrition.supplementation) || 'Não informado.' },
      ];

      const defaultGuaranteeLabels = [
        'Umidade (Máx.)',
        'Proteína Bruta (Min.)',
        'Extrato Etéreo (Min.)',
        'Matéria Fibrosa (Máx.)',
        'Matéria Mineral (Máx.)',
        'Cálcio (Min.)',
        'Cálcio (Máx.)',
        'Fósforo (Min.)',
        'Mananoligossacarídeo',
        'Frutooligossacarídeo',
        'Energia Metabolizável (Kcal/kg)',
      ];

      const guarantees = Array.isArray(nutrition.guarantees)
        ? nutrition.guarantees
            .map((item) => ({
              label: String(item?.label || '').trim(),
              value: this.formatNutritionValue(item?.value),
            }))
            .filter((item) => item.label)
        : [];

      const guaranteesByLabel = new Map(
        guarantees.map((item) => [this.normalizeText(item.label), item.value || '-']),
      );

      const normalizedGuarantees = defaultGuaranteeLabels.map((label) => ({
        label,
        value: guaranteesByLabel.get(this.normalizeText(label)) || '-',
      }));

      if (this.nutritionModalTitle) {
        this.nutritionModalTitle.textContent = modalTitle;
      }

      if (flavorColor) {
        this.nutritionModal.style.setProperty('--tp-nutrition-modal-card-bg', flavorColor);
      } else {
        this.nutritionModal.style.removeProperty('--tp-nutrition-modal-card-bg');
      }

      this.nutritionModalContent.innerHTML = blocks
        .map(
          (block) => `
            <div class="tp-nutricao-change-card__content-block">
              <h3 class="tp-nutricao-change-card__title">${this.escapeHtml(block.title)}</h3>
              ${
                block.description
                  ? `<div class="tp-nutricao-change-card__description">${this.escapeHtml(block.description)}</div>`
                  : ''
              }
            </div>
          `,
        )
        .join('');

      this.nutritionModalGuarantees.innerHTML = normalizedGuarantees
        .map(
          (item) => `
            <div class="tp-nutricao-change-card__guarantee-row">
              <span class="tp-nutricao-change-card__guarantee-label">${this.escapeHtml(item.label)}</span>
              <span class="tp-nutricao-change-card__guarantee-value">${this.escapeHtml(String(item.value).replace(/\.0(?=\s|$)/g, ''))}</span>
            </div>
          `,
        )
        .join('');

      this.nutritionModal.show(opener);
    }

    syncState() {
      this.updateSizeAvailability();

      if (!this.isSizeAvailable(this.selectedSize)) {
        const firstAvailableSize = this.sizeOptions.find((button) => !button.disabled);
        this.selectedSize = firstAvailableSize?.dataset.sizeValue || '';
      }

      this.updateSizeButtons();

      this.flavorRows.forEach((flavor) => {
        flavor.selectedVariant = this.findMatchingVariant(flavor.variants, this.selectedSize);

        if ((!flavor.selectedVariant || !flavor.selectedVariant.available) && flavor.quantity > 0) {
          flavor.quantity = 0;
          if (flavor.qtyValue) {
            flavor.qtyValue.textContent = '0';
          }
        }

        const isActive = flavor.quantity > 0;
        flavor.row.classList.toggle('is-active', isActive);
        flavor.row.classList.toggle('is-disabled', !flavor.selectedVariant || !flavor.selectedVariant.available);

        if (isActive && flavor.row.dataset.flavorColor) {
          flavor.row.style.setProperty('--tp-row-accent', flavor.row.dataset.flavorColor);
          flavor.row.style.setProperty('--tp-row-accent-border', flavor.row.dataset.flavorColor);
        } else {
          flavor.row.style.removeProperty('--tp-row-accent');
          flavor.row.style.removeProperty('--tp-row-accent-border');
        }

        if (flavor.decrease) {
          flavor.decrease.disabled = flavor.quantity === 0;
        }

        if (flavor.increase) {
          flavor.increase.disabled = !flavor.selectedVariant || !flavor.selectedVariant.available;
        }
      });

      this.renderSummary();
      this.renderTierState();
    }

    findMatchingVariant(variants, selectedSize) {
      if (!Array.isArray(variants) || !variants.length) return null;

      const normalizedSize = this.normalizeText(selectedSize);
      const directMatch = variants.find((variant) => {
        const options = Array.isArray(variant.options) ? variant.options : [];
        const title = this.normalizeText(variant.title || '');
        return (
          options.some((option) => this.normalizeText(option) === normalizedSize) ||
          title === normalizedSize
        );
      });

      // Each flavor must match the currently selected size exactly.
      // If that size does not exist or is unavailable, the flavor row stays disabled.
      return directMatch || null;
    }

    normalizeText(value) {
      return String(value || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    }

    escapeHtml(value) {
      return String(value || '')
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#39;');
    }

    formatNutritionText(value) {
      const formattedValue = this.formatNutritionValue(value);
      const parsedList = this.parseSerializedList(formattedValue);
      if (parsedList.length) {
        return parsedList.join(', ');
      }

      return formattedValue.replace(/,\s*/g, ', ');
    }

    formatNutritionValue(value) {
      if (value == null) return '';
      if (typeof value === 'string') return value.trim();
      if (typeof value === 'number' || typeof value === 'boolean') return String(value);

      if (Array.isArray(value)) {
        return value
          .map((item) => this.formatNutritionValue(item))
          .filter(Boolean)
          .join(', ');
      }

      if (typeof value === 'object') {
        const amount = value.amount ?? value.value ?? value.number ?? '';
        const unit = value.unit ?? value.unit_name ?? value.unitName ?? '';

        if (amount !== '' && unit) {
          return `${amount} ${unit}`.trim();
        }

        if (typeof value.value === 'string' || typeof value.value === 'number') {
          return String(value.value).trim();
        }

        const parts = Object.values(value)
          .map((item) => this.formatNutritionValue(item))
          .filter(Boolean);

        return parts.join(' ');
      }

      return String(value).trim();
    }

    parseSerializedList(value) {
      const trimmedValue = String(value || '').trim();
      if (!trimmedValue.startsWith('[') || !trimmedValue.endsWith(']')) return [];

      try {
        const parsedValue = JSON.parse(trimmedValue);
        if (!Array.isArray(parsedValue)) return [];

        return parsedValue
          .map((item) => this.formatNutritionValue(item))
          .filter(Boolean);
      } catch (error) {
        return [];
      }
    }

    renderSummary() {
      const totals = this.flavorRows.reduce(
        (accumulator, flavor) => {
          if (!flavor.selectedVariant || !flavor.selectedVariant.available || flavor.quantity <= 0) {
            return accumulator;
          }

          const basePrice = Number(flavor.selectedVariant.price || 0);
          const comparePrice = Number(flavor.selectedVariant.compare_at_price || basePrice);

          accumulator.quantity += flavor.quantity;
          accumulator.current += basePrice * flavor.quantity;
          accumulator.compare += comparePrice * flavor.quantity;

          return accumulator;
        },
        {
          quantity: 0,
          current: 0,
          compare: 0,
        },
      );

      const activeTier = this.getActiveTier(totals.quantity);
      const tierDiscount = Number(activeTier?.discount || 0);
      const discountedCurrent = Math.round(totals.current * (1 - tierDiscount / 100));
      const compareTotal = Math.max(totals.compare, totals.current);
      const hasCompare = compareTotal > discountedCurrent;

      const hasItems = totals.quantity > 0;

      if (this.summaryCurrent) {
        this.summaryCurrent.textContent = hasItems ? this.formatMoney(discountedCurrent) : '';
        this.summaryCurrent.hidden = !hasItems;
      }

      if (this.summaryCompare) {
        this.summaryCompare.textContent = this.formatMoney(compareTotal);
        this.summaryCompare.hidden = !hasItems || !hasCompare;
      }

      if (this.summaryBadge) {
        const savingsPercent = hasCompare && compareTotal > 0
          ? Math.round(((compareTotal - discountedCurrent) / compareTotal) * 100)
          : tierDiscount;
        const shouldShowBadge = savingsPercent > 0 && hasItems;

        this.summaryBadge.textContent = shouldShowBadge ? `${savingsPercent}% OFF` : '';
        this.summaryBadge.hidden = !shouldShowBadge;
      }

      const canPurchase = this.canPurchase(totals.quantity);

      if (this.buyButton) {
        this.buyButton.disabled = !canPurchase;
      }

      if (this.buyButtonWrap) {
        this.buyButtonWrap.classList.toggle('is-disabled', !canPurchase);
      }

      document.dispatchEvent(new CustomEvent('tp:price-update', {
        detail: {
          current: discountedCurrent,
          compare: hasCompare ? compareTotal : 0,
          discount: hasCompare && compareTotal > 0
            ? Math.round(((compareTotal - discountedCurrent) / compareTotal) * 100)
            : tierDiscount,
          quantity: totals.quantity,
        },
      }));
    }

    renderTierState() {
      const totalQuantity = this.flavorRows.reduce((sum, flavor) => sum + flavor.quantity, 0);
      const activeTier = this.getActiveTier(totalQuantity);
      const activeQuantity = Number(activeTier?.quantity || 0);

      this.tiers.forEach((tier) => {
        const isActive = activeTier ? Number(tier.dataset.tierQuantity) <= activeQuantity : false;
        tier.classList.toggle('is-active', isActive);
      });

      this.updateTierProgress();
    }

    updateTierProgress() {
      if (!this.tierHeader || !this.tiers.length) return;

      const tierCounts = this.tiers
        .map((tier) => tier.querySelector('.tp-main-product__tier-count'))
        .filter(Boolean);

      if (!tierCounts.length) return;

      const headerRect = this.tierHeader.getBoundingClientRect();
      const totalQuantity = this.flavorRows.reduce((sum, flavor) => sum + flavor.quantity, 0);
      const tierQuantities = this.getTierQuantities();

      const lineStart = 0;
      let activePoint = 0;

      if (totalQuantity > 0) {
        const lastIndex = tierCounts.length - 1;

        if (totalQuantity >= tierQuantities[lastIndex]) {
          activePoint = headerRect.width;
        } else {
          const tierCenters = tierCounts.map((count) => {
            const rect = count.getBoundingClientRect();
            return rect.left + rect.width / 2 - headerRect.left;
          });

          let nextIndex = tierQuantities.findIndex((quantity) => totalQuantity <= quantity);

          if (nextIndex === -1) {
            nextIndex = lastIndex;
          }

          const previousIndex = nextIndex - 1;
          const previousQuantity = previousIndex >= 0 ? tierQuantities[previousIndex] : 0;
          const nextQuantity = tierQuantities[nextIndex];
          const previousCenter = previousIndex >= 0 ? tierCenters[previousIndex] : lineStart;
          const nextCenter = tierCenters[nextIndex];
          const range = nextQuantity - previousQuantity;
          const progress = range > 0 ? (totalQuantity - previousQuantity) / range : 0;

          activePoint = previousCenter + (nextCenter - previousCenter) * progress;
        }
      }

      const progressWidth = Math.max(0, activePoint - lineStart);

      this.tierHeader.style.setProperty('--tp-tier-line-start', `${lineStart}px`);
      this.tierHeader.style.setProperty('--tp-tier-progress-width', `${progressWidth}px`);
    }

    getActiveTier(totalQuantity) {
      const tiers = Array.isArray(this.config?.tiers) ? this.config.tiers : [];
      if (!tiers.length) return null;
      if (totalQuantity < Number(tiers[0]?.quantity || 0)) return null;

      return tiers.reduce((selected, tier) => {
        if (totalQuantity >= Number(tier.quantity || 0)) {
          return tier;
        }
        return selected;
      }, tiers[0]);
    }

    formatMoney(cents) {
      return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: 'BRL',
      }).format((Number(cents) || 0) / 100);
    }

    async addSelectionToCart() {
      if (!this.buyButton || this.buyButton.disabled) return;

      const items = this.flavorRows
        .filter((flavor) => flavor.selectedVariant && flavor.selectedVariant.available && flavor.quantity > 0)
        .map((flavor) => ({
          id: flavor.selectedVariant.id,
          quantity: flavor.quantity,
        }));

      if (!items.length) return;

      try {
        this.buyButton.disabled = true;
        this.buyButton.textContent = 'Adicionando...';

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ items }),
        });

        if (!response.ok) {
          throw new Error('Erro ao adicionar itens ao carrinho.');
        }

        this.buyButton.textContent = '✓ Adicionado!';

        try {
          await this.openCartDrawer();
        } catch (drawerError) {
          console.warn('tp-main-product: item added, but cart drawer refresh failed', drawerError);
        }

        setTimeout(() => {
          this.buyButton.disabled = !this.canPurchase(this.getSelectedTotalQuantity());
          this.buyButton.textContent = this.originalBuyLabel;
        }, 1500);
      } catch (error) {
        console.error('tp-main-product:', error);
        this.buyButton.disabled = !this.canPurchase(this.getSelectedTotalQuantity());
        this.buyButton.textContent = 'Erro ao adicionar';

        setTimeout(() => {
          this.buyButton.textContent = this.originalBuyLabel;
        }, 1800);
      }
    }

    async openCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (!drawer) return;

      const response = await fetch(this.cartSectionsUrl);
      const sections = await response.json();

      drawer.classList.remove('is-empty');
      drawer.renderContents({ sections });

      requestAnimationFrame(() => {
        drawer.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
      });
    }

    getSelectedTotalQuantity() {
      return this.flavorRows.reduce((sum, flavor) => sum + flavor.quantity, 0);
    }
  }

  customElements.define('tp-main-product', TpMainProduct);
}
